import React from 'react';
import { Alert } from 'react-bootstrap';
import { IconAlertCircle, IconFileSpreadsheet, IconExternalLink, IconX } from '@tabler/icons-react';
import Swal from 'sweetalert2';
import { useGooglePicker, SHEETS_MIME_TYPE } from '../../hooks/useGooglePicker';

// =============================================================================
// GOOGLE SHEETS CAPABILITY PICKER - Picker for data_collection destination
// =============================================================================
const GoogleSheetsCapabilityPicker = ({ agentId, integrations, config, onChange }) => {
  const selectedSpreadsheetId = config?.spreadsheet_id || '';
  const selectedSpreadsheetName = config?.spreadsheet_name || '';
  const sheetName = config?.sheet_name || 'Sheet1';

  // Find google_sheets or google_workspace integration
  const gsIntegration = (integrations || []).find(
    (i) => (i.integration_type === 'google_sheets' || i.integration_type === 'google_workspace') && i.is_active
  );

  const integrationId = gsIntegration?.id;

  const { openPicker } = useGooglePicker({
    agentId,
    integrationId,
    integrationType: gsIntegration?.integration_type || 'google_sheets',
    mimeTypes: SHEETS_MIME_TYPE,
    multiSelect: false,
    onSelect: (files) => {
      if (files.length > 0) {
        const file = files[0];
        onChange('spreadsheet_id', file.id);
        onChange('spreadsheet_name', file.name);
      }
    },
    onError: (msg) => {
      Swal.fire({ icon: 'error', title: 'Erro', text: msg });
    },
  });

  const handleSheetNameChange = (e) => {
    onChange('sheet_name', e.target.value);
  };

  const handleClear = () => {
    onChange('spreadsheet_id', '');
    onChange('spreadsheet_name', '');
    onChange('sheet_name', 'Sheet1');
  };

  // No integration found
  if (!gsIntegration) {
    return (
      <Alert variant="warning" className="mt-2 mb-3 d-flex align-items-center gap-2">
        <IconAlertCircle size={18} />
        <small>Configure a integração Google Sheets na aba <strong>Integrações</strong> antes de usar este destino.</small>
      </Alert>
    );
  }

  return (
    <div className="mb-3" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {/* Spreadsheet selector via Picker */}
      <div>
        <label style={{ fontWeight: 500, fontSize: '13px', color: '#94a3b8', marginBottom: '6px', display: 'block' }}>
          Planilha
        </label>

        {selectedSpreadsheetId ? (
          <div style={{
            display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 12px',
            border: '1px solid #dee2e6', borderRadius: '8px', background: '#f9fafb',
          }}>
            <IconFileSpreadsheet size={18} style={{ color: '#0f9d58', flexShrink: 0 }} />
            <span style={{ flex: 1, fontSize: '13px', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {selectedSpreadsheetName || selectedSpreadsheetId}
            </span>
            <button
              onClick={openPicker}
              style={{
                background: 'none', border: '1px solid #d1d5db', borderRadius: '6px',
                padding: '4px 10px', cursor: 'pointer', fontSize: '12px', color: '#4b5563',
              }}
            >
              Trocar
            </button>
            <button
              onClick={handleClear}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: '4px' }}
              title="Remover seleção"
            >
              <IconX size={14} />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={openPicker}
            style={{
              width: '100%', padding: '12px', border: '2px dashed #d1d5db', borderRadius: '8px',
              background: '#fafafa', cursor: 'pointer', display: 'flex', alignItems: 'center',
              justifyContent: 'center', gap: '8px', fontSize: '13px', fontWeight: 500, color: '#4b5563',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#0f9d58'; e.currentTarget.style.background = '#f0fdf4'; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.background = '#fafafa'; }}
          >
            <IconFileSpreadsheet size={16} style={{ color: '#0f9d58' }} />
            Selecionar planilha do Google
          </button>
        )}
      </div>

      {/* Sheet name */}
      {selectedSpreadsheetId && (
        <div>
          <label style={{ fontWeight: 500, fontSize: '13px', color: '#94a3b8', marginBottom: '6px', display: 'block' }}>
            Nome da Aba
          </label>
          <input
            type="text"
            value={sheetName}
            onChange={handleSheetNameChange}
            placeholder="Sheet1"
            style={{
              width: '100%', padding: '8px 12px', border: '1px solid #dee2e6',
              borderRadius: '6px', fontSize: '14px', outline: 'none',
            }}
          />
          <div style={{ fontSize: '11px', color: '#999', marginTop: '4px' }}>
            Nome exato da aba na planilha onde os dados serão gravados
          </div>
        </div>
      )}

      {/* Link to open spreadsheet */}
      {selectedSpreadsheetId && (
        <a
          href={`https://docs.google.com/spreadsheets/d/${selectedSpreadsheetId}/edit`}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '6px',
            fontSize: '13px', color: '#0f9d58', textDecoration: 'none',
          }}
        >
          <IconExternalLink size={14} />
          Abrir planilha no Google Sheets
        </a>
      )}
    </div>
  );
};

export default GoogleSheetsCapabilityPicker;
